import { useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";

import { defaultGreeting, whatsappLink, whatsappNumber } from "../lib/links";
import { Button, cx } from "./ui";

/**
 * "Message them on WhatsApp".
 *
 * A box with the message in it, rather than a button that fires a fixed
 * greeting. The message is the part somebody wants to control -- "your
 * appointment is confirmed for Tuesday" is worth sending and "Hi Anita," is
 * not -- and `wa.me` then opens WhatsApp with it ready to send.
 *
 * We do not send it ourselves. There is no WhatsApp Business API credential in
 * this project, and a button that implied otherwise would fail silently for a
 * customer rather than visibly for us.
 */
export function WhatsAppComposer({
  phone,
  firstName,
  onClose,
}: {
  phone: string | null | undefined;
  firstName?: string | null;
  onClose: () => void;
}) {
  const [message, setMessage] = useState(defaultGreeting(firstName));
  const href = whatsappLink(phone, message);
  const dialable = whatsappNumber(phone);

  // No usable number: say so rather than opening a link that goes nowhere.
  if (!dialable || !href) {
    return (
      <div className="rounded-lg bg-amber-50 p-3 ring-1 ring-inset ring-amber-600/20">
        <p className="text-sm font-medium text-amber-900">No WhatsApp number</p>
        <p className="mt-0.5 text-xs text-amber-800">
          {phone
            ? `"${phone}" is too short to be an international number. WhatsApp needs the country code.`
            : "Add a phone number to this lead to message them on WhatsApp."}
        </p>
        <Button onClick={onClose} className="mt-2">
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-emerald-50 p-3 ring-1 ring-inset ring-emerald-600/20">
      <div className="mb-2 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-900">
          <MessageCircle size={14} /> Message on WhatsApp
        </p>
        <button
          onClick={onClose}
          aria-label="Close WhatsApp message"
          className="rounded p-0.5 text-emerald-700 hover:bg-emerald-100"
        >
          <X size={13} />
        </button>
      </div>

      <textarea
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        rows={3}
        aria-label="WhatsApp message"
        className="w-full rounded-md border-0 bg-white px-2.5 py-2 text-sm ring-1 ring-inset ring-emerald-600/20 placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-600/40"
      />

      <p className="mt-1 text-xs text-emerald-800">
        Opens WhatsApp with this ready to send — you press send there.
      </p>

      <div className="mt-2 flex items-center gap-2">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={cx(
            "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-white transition",
            message.trim()
              ? "bg-emerald-600 hover:bg-emerald-700"
              : "pointer-events-none bg-emerald-600/40",
          )}
        >
          <Send size={14} /> Open WhatsApp
        </a>
        <Button onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}